import type { ReactNode } from "react";
import { type ActionList, ActionListView } from "./action-list-view";
import { Box } from "./box";
import { Breadcrumbs, CRUMB_VARIANTS, type Crumb } from "./breadcrumbs";
import { ButtonLinkDocs } from "./button";
import { CostEstimateInfoIcon } from "./cost-estimate-tooltip";
import { Group } from "./group";
import { IconAlertTriangle } from "./icons";
import { LoadingSpinner } from "./loading";
import { type TabItem, Tabs } from "./tabs";

interface HeaderProps {
  breadcrumbs?: Crumb[];
  actions?: ActionList;
  detailsBox?: React.ReactNode;
  title: string;
  titleLabel?: string;
  tabs?: TabItem[];
  isError: boolean;
  message: string;
  meta: Record<string, any>;
  lastBreadcrumbTo?: string;
}

const DetailErrorBox = ({
  message,
  meta = {},
}: { message: string; meta: Record<string, any> }) => {
  return (
    <Box className="flex items-center justify-center">
      <h1 className="text-lg text-red-500 font-semibold">
        <IconAlertTriangle
          className="inline pr-3 mb-1"
          style={{ width: 32 }}
          color="#AD1A1A"
        />
        <span className="ml-2">{message}</span>
      </h1>

      <div>
        {Object.keys(meta).map((key) => {
          return (
            <div key={key}>
              {key}: {meta[key]}
            </div>
          );
        })}
      </div>
    </Box>
  );
};

export const DetailPageHeaderView = ({
  breadcrumbs,
  title,
  titleLabel,
  detailsBox,
  actions,
  tabs,
  isError,
  message,
  meta,
  lastBreadcrumbTo,
}: HeaderProps) => {
  if (isError) {
    return <DetailErrorBox message={message} meta={meta} />;
  }

  return (
    <div className="flex flex-col">
      <div className="pb-[11px]">
        {breadcrumbs ? (
          lastBreadcrumbTo ? (
            <Breadcrumbs
              crumbs={[
                ...breadcrumbs,
                {
                  to: lastBreadcrumbTo,
                  name: title,
                  label: titleLabel,
                  crumbVariant: titleLabel
                    ? CRUMB_VARIANTS.UPPERCASE
                    : undefined,
                },
              ]}
            />
          ) : (
            <Breadcrumbs
              crumbs={[
                ...breadcrumbs,
                {
                  to: null,
                  name: title,
                  label: titleLabel,
                  crumbVariant: titleLabel
                    ? CRUMB_VARIANTS.UPPERCASE
                    : undefined,
                },
              ]}
            />
          )
        ) : null}
      </div>

      <div className="flex items-center">
        {actions && <ActionListView actions={actions} />}
      </div>

      <div>{detailsBox ? detailsBox : null}</div>

      {tabs ? (
        <div className="pt-4">
          <Tabs tabs={tabs} />
        </div>
      ) : null}
    </div>
  );
};

export function DetailHeader({ children }: { children: ReactNode }) {
  return <Box className="flex flex-col gap-3">{children}</Box>;
}

export function DetailTitleBar({
  title,
  icon,
  otherButtonsBeforeDocs,
  docsUrl = "",
  isLoading = false,
}: {
  title: string;
  icon?: ReactNode;
  otherButtonsBeforeDocs?: ReactNode;
  docsUrl?: string;
  isLoading?: boolean;
}) {
  return (
    <div className="flex justify-between items-center">
      <Group size="sm" variant="horizontal" className="items-center">
        {icon ? <div className="w-[32px] h-[32px]">{icon}</div> : null}
        <h1 className="text-lg text-gray-500">{title}</h1>
        <LoadingSpinner show={isLoading} />
      </Group>

      <div className="flex">
        {otherButtonsBeforeDocs ? otherButtonsBeforeDocs : null}
        {docsUrl ? <ButtonLinkDocs href={docsUrl} /> : null}
      </div>
    </div>
  );
}

export function DetailInfoGrid({
  children,
  columns = 2,
}: { children: ReactNode; columns?: number }) {
  let col = "md:grid-cols-2";
  if (columns === 3) {
    col = "md:grid-cols-3";
  } else if (columns >= 4) {
    col = `lg:grid-cols-${columns}`;
  }
  return <div className={`grid ${col} grid-cols-1 gap-4`}>{children}</div>;
}

export function DetailInfoItem({
  title,
  titleTooltip,
  children,
  isLoading = false,
}: {
  title: string;
  titleTooltip?: string;
  children: ReactNode;
  isLoading?: boolean;
}) {
  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
        {title}
        {titleTooltip != null ? (
          <CostEstimateInfoIcon
            text={titleTooltip}
            ariaLabel={`More information: ${title}`}
          />
        ) : null}
        <LoadingSpinner
          variant="xs"
          className="inline-block ml-2"
          show={isLoading}
        />
      </h3>
      <div className="break-all">{children}</div>
    </div>
  );
}
